<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-lg font-medium">Mes Moyens de Paiement</h3>
      <UButton
        icon="i-lucide-credit-card"
        size="sm"
        color="black"
        @click="openAddCard"
      >
        Ajouter une carte
      </UButton>
    </div>

    <!-- List Methods -->
    <div v-if="pending" class="space-y-4">
      <USkeleton v-for="i in 2" :key="i" class="h-16 w-full" />
    </div>

    <div
      v-else-if="methods && methods.data && methods.data.length > 0"
      class="space-y-4"
    >
      <div
        v-for="method in methods.data"
        :key="method.id"
        class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 flex justify-between items-center"
      >
        <div class="flex items-center gap-4">
          <div
            class="w-10 h-6 bg-gray-200 rounded flex items-center justify-center text-xs font-bold uppercase"
          >
            {{ method.card.brand }}
          </div>
          <div>
            <p class="font-medium text-gray-900 dark:text-white">
              •••• {{ method.card.last4 }}
            </p>
            <p class="text-xs text-gray-500">
              Expire le {{ method.card.exp_month }}/{{ method.card.exp_year }}
            </p>
          </div>
        </div>
        <UButton
          icon="i-lucide-trash-2"
          color="red"
          variant="ghost"
          size="sm"
          :loading="detaching === method.id"
          @click="detachMethod(method.id)"
        />
      </div>
    </div>

    <div v-else class="text-center py-10 text-gray-500">
      Aucun moyen de paiement enregistré.
    </div>

    <!-- Add Card Modal -->
    <UModal v-model:open="isCardModalOpen">
      <template #content>
        <UCard>
          <template #header>
            <div class="flex items-center justify-between">
              <h3
                class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
              >
                Ajouter une carte
              </h3>
              <UButton
                color="gray"
                variant="ghost"
                icon="i-heroicons-x-mark-20-solid"
                class="-my-1"
                @click="isCardModalOpen = false"
              />
            </div>
          </template>

          <div v-if="loadingSecret" class="flex justify-center py-8">
            <UIcon name="i-lucide-loader-2" class="animate-spin w-8 h-8" />
          </div>

          <form v-else id="setup-form" @submit.prevent="handleSetup">
            <div id="setup-element" class="mb-6" />
            <div v-if="errorMessage" class="mb-4 text-red-500 text-sm">
              {{ errorMessage }}
            </div>
            <div class="flex justify-end pt-4">
              <UButton
                type="submit"
                :loading="processing"
                color="main"
                size="lg"
                :disabled="!stripe || !elements"
              >
                Enregistrer la carte
              </UButton>
            </div>
          </form>
        </UCard>
      </template>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import { loadStripe } from "@stripe/stripe-js";
import type { Stripe, StripeElements } from "@stripe/stripe-js";

const config = useRuntimeConfig();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { token } = useAuth();
const toast = useToast();

const isCardModalOpen = ref(false);
const loadingSecret = ref(false);
const processing = ref(false);
const detaching = ref<string | null>(null);
const errorMessage = ref("");

const stripe = ref<Stripe | null>(null);
const elements = ref<StripeElements | null>(null);

const methods = reactive({ data: [] as any[] }); // eslint-disable-line @typescript-eslint/no-explicit-any
const pending = ref(true);

const fetchMethods = async () => {
  pending.value = true;
  try {
    const { $api } = useNuxtApp();
    const result = await $api.get<any>("/payment/methods", "include"); // eslint-disable-line @typescript-eslint/no-explicit-any
    methods.data = result.data || [];
  } catch (e) {
    console.error(e);
  } finally {
    pending.value = false;
  }
};

onMounted(() => {
  fetchMethods();
});

const openAddCard = async () => {
  isCardModalOpen.value = true;
  loadingSecret.value = true;
  errorMessage.value = "";

  try {
    // 1. Load Stripe
    if (!stripe.value) {
      stripe.value = await loadStripe(
        config.public.stripePublicKey as string,
      );
    }

    // 2. Get Setup Intent Secret
    const { $api } = useNuxtApp();
    console.log("Requesting setup intent...");
    const response = await $api.post<any>( // eslint-disable-line @typescript-eslint/no-explicit-any
      "/payment/setup-intent",
      {},
      "include",
    );
    console.log("Setup intent response:", response);
    const { client_secret } = response;

    // 3. Mount Elements
    if (stripe.value && client_secret) {
      console.log("Mounting Stripe elements...");
      elements.value = stripe.value.elements({
        clientSecret: client_secret,
        appearance: { theme: "stripe" },
      });

      // Wait for modal to render content
      // Reveal the form first so the DOM element exists
      loadingSecret.value = false;
      await nextTick();

      const el = elements.value?.create("payment");
      el?.mount("#setup-element");
      console.log("Stripe elements mounted.");
    } else {
      console.error("Missing stripe instance or client_secret", {
        stripe: !!stripe.value,
        client_secret,
      });
    }
  } catch (e) {
    console.error("Error in openAddCard:", e);
    toast.add({ title: "Erreur d'initialisation", color: "red" });
    isCardModalOpen.value = false;
  }
};

const handleSetup = async () => {
  if (!stripe.value || !elements.value) return;

  processing.value = true;
  errorMessage.value = "";

  console.log("Confirming setup...");
  const { error } = await stripe.value.confirmSetup({
    elements: elements.value,
    confirmParams: {
      return_url: window.location.href, // Redirect back here
    },
    redirect: "if_required",
  });

  if (error) {
    console.error("Stripe confirmSetup error:", error);
    errorMessage.value = error.message || "Erreur inconnue";
    processing.value = false;
  } else {
    // Success (without redirect)
    console.log("Setup confirmed successfully.");
    toast.add({ title: "Carte ajoutée avec succès" });
    isCardModalOpen.value = false;
    await fetchMethods();
    processing.value = false;
  }
};

const detachMethod = async (id: string) => {
  if (!confirm("Supprimer cette carte ?")) return;
  detaching.value = id;
  try {
    const { $api } = useNuxtApp();
    await $api.del(`/payment/methods/${id}`, "include");
    toast.add({ title: "Carte supprimée" });
    await fetchMethods();
  } catch {
    toast.add({ title: "Erreur", color: "red" });
  } finally {
    detaching.value = null;
  }
};
</script>
